// 📍 src/pages/Affiliate.jsx
//
// The money-pointing dashboard. Oráku recommends products in chat with
// tappable Amazon/Jumia buttons; every tap is logged by the
// `recordAffiliateClick` function into:
//   • affiliateStats/{YYYY-MM-DD}  — daily counters per program
//   • affiliateClicks              — one row per tap (uid, program, query)
// ACTUAL EARNINGS live on each network's own dashboard (Amazon Associates,
// Jumia KOL) — clicks here tell you which program and which products are
// driving them. The config card writes app_config/affiliate: paste the
// Amazon tracking tag (e.g. orakusmartai-20) and the Jumia link template
// ({q} = search words) the moment the network approves you.

import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, getDoc, setDoc, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, Card, StatCard, GoldBtn, Spinner } from '../components/UI';

export default function Affiliate() {
  const [stats, setStats] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [cfg, setCfg] = useState({ enabled: true, amazonTag: '', jumiaTemplate: '' });
  const [loading, setLoading] = useState(true);
  const [saveMsg, setSaveMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statSnap, clickSnap, cfgSnap] = await Promise.all([
        getDocs(query(collection(db, 'affiliateStats'), orderBy('__name__', 'desc'), limit(30))),
        getDocs(query(collection(db, 'affiliateClicks'), orderBy('at', 'desc'), limit(50))),
        getDoc(doc(db, 'app_config', 'affiliate')),
      ]);
      setStats(statSnap.docs.map(d => ({ day: d.id, ...d.data() })));
      setClicks(clickSnap.docs.map(d => d.data()));
      if (cfgSnap.exists()) setCfg(prev => ({ ...prev, ...cfgSnap.data() }));
    } catch (e) { console.error('affiliate load failed:', e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function saveCfg() {
    setSaveMsg('');
    try {
      await setDoc(doc(db, 'app_config', 'affiliate'), {
        enabled: cfg.enabled !== false,
        amazonTag: (cfg.amazonTag || '').trim(),
        jumiaTemplate: (cfg.jumiaTemplate || '').trim(),
        flightsTemplate: (cfg.flightsTemplate || '').trim(),
        hotelsTemplate: (cfg.hotelsTemplate || '').trim(),
      }, { merge: true });
      setSaveMsg('✓ Saved — live within ~10 minutes (server cache).');
    } catch (e) { setSaveMsg('✗ ' + e.message); }
  }

  const totals = stats.reduce((a, s) => ({
    total: a.total + (s.total || 0),
    amazon: a.amazon + (s.amazon || 0),
    jumia: a.jumia + (s.jumia || 0),
    flights: a.flights + (s.flights || 0),
    hotels: a.hotels + (s.hotels || 0),
  }), { total: 0, amazon: 0, jumia: 0, flights: 0, hotels: 0 });

  if (loading) return <Spinner />;

  const inp = { width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14,
    background: 'var(--bg)', color: 'var(--text1)', border: '1px solid var(--border, #333)' };
  const lab = { fontSize: 12, fontWeight: 700, color: 'var(--text2, #999)', display: 'block',
    margin: '12px 0 6px' };

  return (
    <div style={{ padding: 24 }}>
      <PageHeader title="Affiliate" subtitle="Oráku points, networks pay — clicks tracked here, revenue on each network's dashboard" />

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatCard label="Clicks (30 days)" value={totals.total} icon="🛒" />
        <StatCard label="Amazon" value={totals.amazon} icon="📦" />
        <StatCard label="Flights" value={totals.flights} icon="✈️" />
        <StatCard label="Hotels" value={totals.hotels} icon="🏨" />
        <StatCard label="Jumia" value={totals.jumia} icon="🟠" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
        <Card title="Network configuration">
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, margin: '6px 0 4px' }}>
            <input type="checkbox" checked={cfg.enabled !== false}
              onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} />
            Shopping recommendations enabled
          </label>
          <span style={lab}>Amazon tracking tag (Associates → e.g. orakusmartai-20)</span>
          <input style={inp} value={cfg.amazonTag || ''} placeholder="orakusmartai-20"
            onChange={e => setCfg({ ...cfg, amazonTag: e.target.value })} />
          <span style={lab}>Flights link template ({'{q}'} = route words; empty = Google Flights, no commission)</span>
          <input style={inp} value={cfg.flightsTemplate || ''}
            placeholder="https://www.expedia.com/shop/orakusmartai-20?q={q}"
            onChange={e => setCfg({ ...cfg, flightsTemplate: e.target.value })} />
          <span style={lab}>Hotels link template ({'{q}'} = destination words)</span>
          <input style={inp} value={cfg.hotelsTemplate || ''}
            placeholder="https://www.expedia.com/shop/orakusmartai-20?q={q}"
            onChange={e => setCfg({ ...cfg, hotelsTemplate: e.target.value })} />
          <span style={lab}>Jumia link template ({'{q}'} = search words; from your KOL dashboard — later)</span>
          <input style={inp} value={cfg.jumiaTemplate || ''}
            placeholder="https://kol.jumia.com/api/click?...&q={q}"
            onChange={e => setCfg({ ...cfg, jumiaTemplate: e.target.value })} />
          <div style={{ marginTop: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
            <GoldBtn onClick={saveCfg}>Save</GoldBtn>
            {saveMsg && <span style={{ fontSize: 13, color: saveMsg.startsWith('✓') ? '#7BC49A' : '#E57373' }}>{saveMsg}</span>}
          </div>
        </Card>

        <Card title="Clicks per day">
          {stats.length === 0 && <div style={{ color: 'var(--text2)', fontSize: 13 }}>No clicks yet — they appear the moment users tap a shop button.</div>}
          {stats.map(s => (
            <div key={s.day} style={{ display: 'flex', justifyContent: 'space-between',
              padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13 }}>
              <span>{s.day}</span>
              <span><b style={{ color: 'var(--gold, #D4AF37)' }}>{s.total || 0}</b>
                <span style={{ color: 'var(--text2)', marginLeft: 10 }}>amazon {s.amazon || 0} · jumia {s.jumia || 0}</span></span>
            </div>
          ))}
        </Card>

        <Card title="Latest clicks (what people shop for)">
          {clicks.length === 0 && <div style={{ color: 'var(--text2)', fontSize: 13 }}>Nothing yet.</div>}
          {clicks.map((c, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10,
              padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.program === 'jumia' ? '🟠' : '📦'} {c.query || '(no query)'}
              </span>
              <span style={{ color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                {c.at?.toDate?.().toLocaleString?.() || ''}
              </span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
